import { useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemAvatar, ListItemButton, ListItemText, Tab, Tabs, TextField, Typography } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DialpadIcon from "@mui/icons-material/Dialpad";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";

import { ContactsAPI, type Contact } from "@crm-src/lib/contacts-api";
import { ensureCrmSession } from "@crm-src/lib/api";

import { useCallController } from "./CallProvider.js";
import { Keypad } from "./call-window/Keypad.js";
import { useChatOverview } from "../lib/chat/hooks.js";
import { useCan } from "../auth/permissions.js";
import { useRealtime } from "../lib/notifications/RealtimeProvider.js";
import { UserAvatar } from "../components/UserAvatar.js";
import type { PresenceState } from "../components/PresenceDot.js";

/** Order presence puts the reachable people first: online → away → dnd →
 * offline. Same buckets the chat roster uses, so a person sorts identically
 * here and there. */
const PRESENCE_RANK: Record<PresenceState, number> = {
  active: 0,
  away: 1,
  dnd: 2,
  offline: 3,
};
/** Human label shown under each staff name in the call picker. */
const PRESENCE_LABEL: Record<PresenceState, string> = {
  active: "Online",
  away: "Away",
  dnd: "Do not disturb",
  offline: "Offline",
};

/** A chat staff member you can call internally. */
interface StaffTarget {
  user_id: number;
  name: string;
}
/** A PSTN number to dial (from the keypad or a CRM contact). `label` is what we
 * show in the tray (the contact's name, or the number itself). */
interface NumberTarget {
  number: string;
  label: string;
}

type LauncherTab = "staff" | "keypad" | "contacts";

/**
 * Call Launcher — the top-nav "start a call" dialog.
 *
 * Three ways to compose a call into ONE recipients tray, then launch into the
 * same `CallWindow` chat uses:
 *   • Staff   — multi-select chat staff (internal group call via `ringGroup`).
 *   • Keypad  — dial an arbitrary external phone number (PSTN).
 *   • Contacts— pick a CRM contact; their phone becomes a PSTN target.
 *               (Tab hidden unless the user holds `crm:contacts` + `crm:view`,
 *               so a user without CRM access never triggers `/api/crm/session`.)
 *
 * You can stack several of each → "dial out to multiple at once". Launch calls
 * `controller.startCall({ staff, numbers })`: any staff are rung; any numbers
 * ride along and are auto-dialled once the call connects (a phone-only call
 * spins up a solo room — see CallProvider.startCall).
 */
export function CallLauncher({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { startCall } = useCallController();
  // CRM contacts are gated on the page permission AND the CRM entry gate, so a
  // user without `crm:view` can never trigger the CRM session bootstrap (which
  // would 403 → log them out). Both hooks are called unconditionally (a `&&`
  // short-circuit would skip the second hook — a rules-of-hooks violation).
  const hasContactsPage = useCan("crm:contacts");
  const hasCrmView = useCan("crm:view");
  const canSeeContacts = hasContactsPage && hasCrmView;

  const [tab, setTab] = useState<LauncherTab>("staff");
  const [staff, setStaff] = useState<StaffTarget[]>([]);
  const [numbers, setNumbers] = useState<NumberTarget[]>([]);
  const [staffSearch, setStaffSearch] = useState("");

  // Reset everything when the dialog closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setTab("staff");
      setStaff([]);
      setNumbers([]);
      setStaffSearch("");
      setCrmReady(false);
      setContacts([]);
      setContactSearch("");
    }
  }, [open]);

  // ── Staff source (chat directory) + live presence ──────────────────────────
  const overview = useChatOverview(open);
  // Live who's-online set from the always-on realtime socket — the SAME source
  // the chat roster reads, so presence agrees between the two surfaces.
  const { online } = useRealtime();
  const presence = useMemo(() => {
    const map = new Map<number, PresenceState>();
    for (const e of online) map.set(e.user_id, e.status);
    return map;
  }, [online]);
  const allStaff = useMemo(() => overview.data?.contacts ?? [], [overview.data?.contacts]);
  const filteredStaff = useMemo(() => {
    const q = staffSearch.trim().toLowerCase();
    const matched = q
      ? allStaff.filter((c) => c.display_name.toLowerCase().includes(q))
      : allStaff;
    // Surface the reachable people first (online → away → dnd → offline); the
    // sort is stable so within a bucket the directory order is preserved.
    return [...matched].sort(
      (a, b) =>
        PRESENCE_RANK[presence.get(a.user_id) ?? "offline"] -
        PRESENCE_RANK[presence.get(b.user_id) ?? "offline"],
    );
  }, [allStaff, staffSearch, presence]);
  const onlineCount = useMemo(
    () => allStaff.filter((c) => (presence.get(c.user_id) ?? "offline") !== "offline").length,
    [allStaff, presence],
  );

  const toggleStaff = useCallback((c: { user_id: number; display_name: string }) => {
    setStaff((prev) =>
      prev.some((s) => s.user_id === c.user_id)
        ? prev.filter((s) => s.user_id !== c.user_id)
        : [...prev, { user_id: c.user_id, name: c.display_name }],
    );
  }, []);

  // ── Number targets (keypad + contacts) ─────────────────────────────────────
  const addNumber = useCallback((number: string, label?: string) => {
    const normalized = number.trim();
    if (!normalized) return;
    setNumbers((prev) =>
      prev.some((n) => n.number === normalized)
        ? prev
        : [...prev, { number: normalized, label: label ?? normalized }],
    );
  }, []);

  const removeNumber = useCallback((number: string) => {
    setNumbers((prev) => prev.filter((n) => n.number !== number));
  }, []);

  // ── Contacts source (CRM) ──────────────────────────────────────────────────
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(false);
  // The launcher opens OUTSIDE the /crm runtime, so the CRM session token isn't
  // minted yet (and the lazy 401→rebootstrap path doesn't reliably cover a cold
  // non-CRM route). Mint it once when the Contacts tab is first opened — exactly
  // as `CrmSessionBootstrap` does on /crm entry — BEFORE any crm-api call.
  const [crmReady, setCrmReady] = useState(false);

  useEffect(() => {
    if (!open || tab !== "contacts" || !canSeeContacts || crmReady) return;
    let cancelled = false;
    setContactsLoading(true);
    setContactsError(false);
    void ensureCrmSession()
      .then(() => {
        if (!cancelled) setCrmReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setContactsError(true);
          setContactsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, tab, canSeeContacts, crmReady]);

  useEffect(() => {
    if (!open || tab !== "contacts" || !canSeeContacts || !crmReady) return;
    let cancelled = false;
    setContactsLoading(true);
    setContactsError(false);
    const t = setTimeout(() => {
      // NB: do NOT pass `active` here — crm-api's ContactsController#index calls
      // `@contacts.active` (a scope the Contact model doesn't define) when
      // `params[:active]` is present, which 500s. Filter active + has-phone
      // client-side instead. (The CRM Contacts page never sends `active`.)
      void ContactsAPI.getContacts({ search: contactSearch.trim(), per_page: 25 })
        .then((res) => {
          if (cancelled) return;
          // Only ACTIVE contacts we can actually phone.
          setContacts(res.data.filter((c) => c.active !== false && Boolean(c.phone || c.mobile)));
        })
        .catch(() => {
          if (!cancelled) setContactsError(true);
        })
        .finally(() => {
          if (!cancelled) setContactsLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, tab, canSeeContacts, crmReady, contactSearch]);

  // ── Launch ─────────────────────────────────────────────────────────────────
  const hasTargets = staff.length > 0 || numbers.length > 0;
  const totalTargets = staff.length + numbers.length;

  const handleStart = useCallback(() => {
    if (!hasTargets) return;
    void startCall({ staff, numbers: numbers.map((n) => n.number) });
    onClose();
  }, [hasTargets, startCall, staff, numbers, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ "data-testid": "call-launcher" }}
    >
      <DialogTitle sx={{ pb: 1 }}>Start a call</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v: LauncherTab) => setTab(v)}
          variant="fullWidth"
          sx={{ mb: 1.5 }}
        >
          <Tab
            value="staff"
            label="Staff"
            icon={<GroupsIcon fontSize="small" />}
            iconPosition="start"
            data-testid="call-launcher-tab-staff"
          />
          <Tab
            value="keypad"
            label="Keypad"
            icon={<DialpadIcon fontSize="small" />}
            iconPosition="start"
            data-testid="call-launcher-tab-keypad"
          />
          {canSeeContacts && (
            <Tab
              value="contacts"
              label="Contacts"
              icon={<PersonIcon fontSize="small" />}
              iconPosition="start"
              data-testid="call-launcher-tab-contacts"
              />
          )}
        </Tabs>

        {/* ── Staff tab ── */}
        {tab === "staff" && (
          <Box>
            <TextField
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
              placeholder="Search staff…"
              size="small"
              fullWidth
              inputProps={{ "aria-label": "Search staff", "data-testid": "call-launcher-staff-search" }}
              sx={{ mb: 1 }}
            />
            <Typography
              data-testid="call-launcher-online-count"
              sx={{ px: 1, mb: 0.5, color: "text.secondary", fontSize: 12, fontWeight: 600 }}
            >
              {onlineCount > 0 ? `${onlineCount} online` : "No one online"}
            </Typography>
            <List dense sx={{ maxHeight: 240, overflowY: "auto" }}>
              {filteredStaff.length === 0 && (
                <Typography sx={{ px: 1, py: 2, color: "text.secondary", fontSize: 14 }}>
                  No staff found.
                </Typography>
              )}
              {filteredStaff.map((c) => {
                const selected = staff.some((s) => s.user_id === c.user_id);
                const state = presence.get(c.user_id) ?? "offline";
                return (
                  <ListItemButton
                    key={c.user_id}
                    selected={selected}
                    onClick={() => toggleStaff(c)}
                    data-testid={`call-launcher-staff-${c.user_id}`}
                    data-presence={state}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <UserAvatar name={c.display_name} userId={c.user_id} size={28} presence={state} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={c.display_name}
                      secondary={PRESENCE_LABEL[state]}
                      primaryTypographyProps={{ fontSize: 14 }}
                      secondaryTypographyProps={{
                        fontSize: 12,
                        color: state === "offline" ? "text.disabled" : "text.secondary",
                      }}
                    />
                    {selected && <CheckCircleIcon fontSize="small" color="primary" />}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}

        {/* ── Keypad tab ── */}
        {tab === "keypad" && (
          <Box sx={{ pt: 0.5 }}>
            <Keypad
              onSubmit={(n) => addNumber(n)}
              submitLabel="Add number"
              submitColor="primary"
              submitIcon={<DialpadIcon />}
              testIdPrefix="launcher-dialpad"
            />
          </Box>
        )}

        {/* ── Contacts tab ── */}
        {tab === "contacts" && canSeeContacts && (
          <Box>
            <TextField
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search contacts…"
              size="small"
              fullWidth
              inputProps={{ "aria-label": "Search contacts", "data-testid": "call-launcher-contact-search" }}
              sx={{ mb: 1 }}
            />
            {contactsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : contactsError ? (
              <Typography sx={{ px: 1, py: 2, color: "error.main", fontSize: 14 }}>
                Couldn't load contacts.
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 240, overflowY: "auto" }}>
                {contacts.length === 0 && (
                  <Typography sx={{ px: 1, py: 2, color: "text.secondary", fontSize: 14 }}>
                    No contacts with a phone number.
                  </Typography>
                )}
                {contacts.map((c) => {
                  const phone = c.phone || c.mobile || "";
                  const selected = numbers.some((n) => n.number === phone);
                  return (
                    <ListItemButton
                      key={c.id}
                      selected={selected}
                      onClick={() => addNumber(phone, c.display_name)}
                      data-testid={`call-launcher-contact-${c.id}`}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                          {c.display_name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={c.display_name}
                        secondary={phone}
                        primaryTypographyProps={{ fontSize: 14 }}
                        secondaryTypographyProps={{ fontSize: 12 }}
                      />
                      {selected && <CheckCircleIcon fontSize="small" color="primary" />}
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        )}

        {/* ── Recipients tray ── */}
        <Box
          data-testid="call-launcher-tray"
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            minHeight: 40,
            alignItems: "center",
          }}
        >
          {!hasTargets && (
            <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
              Pick staff, dial a number, or choose a contact to call.
            </Typography>
          )}
          {staff.map((s) => (
            <Chip
              key={`staff-${s.user_id}`}
              data-testid={`call-launcher-chip-staff-${s.user_id}`}
              icon={<PersonIcon />}
              label={s.name || `User ${s.user_id}`}
              onDelete={() => toggleStaff({ user_id: s.user_id, display_name: s.name })}
              size="small"
            />
          ))}
          {numbers.map((n) => (
            <Chip
              key={`number-${n.number}`}
              data-testid={`call-launcher-chip-number-${n.number}`}
              icon={<PhoneIcon />}
              label={n.label}
              onDelete={() => removeNumber(n.number)}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleStart}
          variant="contained"
          color="success"
          startIcon={<CallIcon />}
          disabled={!hasTargets}
          data-testid="call-launcher-start"
          sx={{ borderRadius: 6, px: 2.5 }}
        >
          {totalTargets > 1 ? `Start call (${totalTargets})` : "Start call"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
